import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Component to display "Also Serving Nearby Cities" section on service pages
 * Usage: <NearbyServiceAreas serviceSlug="dental-implants" />
 */
const NearbyServiceAreas = ({ serviceSlug = 'dental-implants', serviceName = '' }) => {
  const [cities, setCities] = useState([]);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await fetch(`${API_URL}/api/service-areas/cities`);
        if (res.ok) {
          const data = await res.json();
          // Get top 12 cities by population (excluding Fort Worth)
          const sortedCities = (data.cities || [])
            .filter(c => c.slug !== 'fort-worth')
            .sort((a, b) => b.population - a.population)
            .slice(0, 12);
          setCities(sortedCities);
        }
      } catch (err) {
        console.error('Error fetching cities:', err);
      }
    };
    fetchCities();
  }, []);

  if (cities.length === 0) return null;

  const displayServiceName = serviceName || serviceSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  return (
    <section className="py-12 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <MapPin className="w-6 h-6 text-teal-600" />
            <h2 className="text-2xl font-bold text-gray-800">
              {displayServiceName} in Your Area
            </h2>
          </div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Fort Worth Dental proudly serves patients from across the DFW metroplex. 
            Find {displayServiceName.toLowerCase()} services near you.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3">
          {cities.map(city => (
            <Link
              key={city.slug}
              to={`/${serviceSlug}-${city.slug}`}
              className="inline-flex items-center bg-white border border-gray-200 px-4 py-2 rounded-full text-sm text-gray-700 hover:border-teal-500 hover:text-teal-600 hover:shadow-md transition-all"
            >
              <span>{city.name}</span>
              <span className="ml-2 text-xs text-gray-400">({city.distance} mi)</span>
            </Link>
          ))}
        </div>

        <div className="text-center mt-6">
          <Link
            to="/areas-we-serve"
            className="inline-flex items-center text-teal-600 hover:text-teal-700 font-semibold"
          >
            View All 53 Cities We Serve →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default NearbyServiceAreas;
